import type { DssAnimation } from "@ease/page-builder";
import { Sparkles, Pointer, Waves } from "lucide-react";
import HoverAnimationPicker from "./hover-animation-picker";
import ScrollEffects from "./scroll-effects";

const ENTRANCE_OPTIONS = [
  { label: "None", value: "none" },
  { label: "Fade In", value: "fade-in" },
  { label: "Slide Up", value: "slide-up" },
  { label: "Slide Down", value: "slide-down" },
  { label: "Slide Left", value: "slide-left" },
  { label: "Slide Right", value: "slide-right" },
  { label: "Scale Up", value: "scale-up" },
  { label: "Zoom In", value: "zoom-in" },
  { label: "Pop In", value: "pop-in" },
  { label: "Rotate In", value: "rotate-in" },
  { label: "Rotate In Left", value: "rotate-in-left" },
  { label: "Rotate In Right", value: "rotate-in-right" },
  { label: "Flip X", value: "flip-x" },
  { label: "Flip Y", value: "flip-y" },
  { label: "Bounce Up", value: "bounce-up" },
  { label: "Skew In", value: "skew-in" },
] as const;

const EASING_OPTIONS = [
  { label: "Ease", value: "ease" },
  { label: "Ease In", value: "ease-in" },
  { label: "Ease Out", value: "ease-out" },
  { label: "Ease In Out", value: "ease-in-out" },
  { label: "Linear", value: "linear" },
] as const;

interface AnimationControlsProps {
  value: DssAnimation | undefined;
  onChange: (value: DssAnimation | undefined) => void;
}

function clampNumber(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

export function AnimationControls({ value, onChange }: AnimationControlsProps) {
  const animation = value ?? {};

  const update = (patch: Partial<DssAnimation>) => {
    const next = { ...animation, ...patch };
    const hasValues = Object.values(next).some((entry) => entry !== undefined && entry !== null && entry !== "" && entry !== "none");
    onChange(hasValues ? next : undefined);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-muted)]/40 p-3">
        <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-[var(--color-foreground)]">
          <Sparkles className="h-3.5 w-3.5" />
          Entrance
        </div>
        <div className="grid grid-cols-2 gap-2">
          <label className="flex flex-col gap-1 text-[10px] font-medium text-[var(--color-muted-foreground)]">
            Animation
            <select
              value={animation.entranceType ?? "none"}
              onChange={(event) => update({ entranceType: event.target.value as DssAnimation["entranceType"] })}
              className="rounded-md border border-[var(--color-border)] bg-[var(--color-card)] px-2 py-1 text-[11px] text-[var(--color-foreground)]"
            >
              {ENTRANCE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-[10px] font-medium text-[var(--color-muted-foreground)]">
            Threshold
            <input
              type="range"
              min="0.05"
              max="0.9"
              step="0.05"
              value={animation.scrollThreshold ?? 0.15}
              onChange={(event) => update({ scrollThreshold: Number(event.target.value) })}
            />
          </label>
          <label className="flex flex-col gap-1 text-[10px] font-medium text-[var(--color-muted-foreground)]">
            Duration
            <input
              type="range"
              min="200"
              max="2000"
              step="50"
              value={animation.durationMs ?? 400}
              onChange={(event) => update({ durationMs: clampNumber(Number(event.target.value), 200, 2000) })}
            />
            <span className="text-[10px] text-[var(--color-muted-foreground)]">{animation.durationMs ?? 400}ms</span>
          </label>
          <label className="flex flex-col gap-1 text-[10px] font-medium text-[var(--color-muted-foreground)]">
            Delay
            <input
              type="range"
              min="0"
              max="2000"
              step="50"
              value={animation.delayMs ?? 0}
              onChange={(event) => update({ delayMs: clampNumber(Number(event.target.value), 0, 2000) })}
            />
            <span className="text-[10px] text-[var(--color-muted-foreground)]">{animation.delayMs ?? 0}ms</span>
          </label>
          <label className="col-span-2 flex flex-col gap-1 text-[10px] font-medium text-[var(--color-muted-foreground)]">
            Easing
            <select
              value={animation.easing ?? "ease"}
              onChange={(event) => update({ easing: event.target.value as DssAnimation["easing"] })}
              className="rounded-md border border-[var(--color-border)] bg-[var(--color-card)] px-2 py-1 text-[11px] text-[var(--color-foreground)]"
            >
              {EASING_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-muted)]/40 p-3">
        <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-[var(--color-foreground)]">
          <Pointer className="h-3.5 w-3.5" />
          Hover
        </div>
        <div className="grid grid-cols-2 gap-2">
          <label className="flex flex-col gap-1 text-[10px] font-medium text-[var(--color-muted-foreground)]">
            Scale
            <input
              type="range"
              min="0.9"
              max="1.2"
              step="0.01"
              value={animation.hoverScale ?? 1}
              onChange={(event) => update({ hoverScale: Number(event.target.value) })}
            />
            <span className="text-[10px] text-[var(--color-muted-foreground)]">{(animation.hoverScale ?? 1).toFixed(2)}x</span>
          </label>
          <label className="flex flex-col gap-1 text-[10px] font-medium text-[var(--color-muted-foreground)]">
            Raise
            <input
              type="range"
              min="-24"
              max="24"
              step="1"
              value={animation.hoverTranslateY ?? 0}
              onChange={(event) => update({ hoverTranslateY: Number(event.target.value) })}
            />
            <span className="text-[10px] text-[var(--color-muted-foreground)]">{animation.hoverTranslateY ?? 0}px</span>
          </label>
          <label className="col-span-2 flex flex-col gap-1 text-[10px] font-medium text-[var(--color-muted-foreground)]">
            Shadow
            <input
              type="text"
              value={animation.hoverShadow ?? ""}
              onChange={(event) => update({ hoverShadow: event.target.value || undefined })}
              placeholder="0 12px 32px rgba(0,0,0,0.18)"
              className="rounded-md border border-[var(--color-border)] bg-[var(--color-card)] px-2 py-1 text-[11px] text-[var(--color-foreground)]"
            />
          </label>
          <div className="col-span-2">
            <HoverAnimationPicker
              value={animation.hoverPreset ?? null}
              onChange={(hoverPreset) => update({ hoverPreset: hoverPreset ?? undefined })}
            />
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-muted)]/40 p-3">
        <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-[var(--color-foreground)]">
          <Waves className="h-3.5 w-3.5" />
          Runtime
        </div>
        <ScrollEffects
          value={animation.scrollEffects ?? null}
          onChange={(scrollEffects) => update({ scrollEffects: scrollEffects ?? undefined })}
        />
        <p className="m-0 text-[11px] leading-5 text-[var(--color-muted-foreground)]">
          Entrance settings bridge into the published scroll-animation runtime. Hover settings generate published
          hover CSS from canonical `_dssStyles.animation`, and scroll effects share the same persisted animation payload.
        </p>
      </div>
    </div>
  );
}
